import { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { X } from 'lucide-react';
import type { Item } from '@perfin/shared';
import { isRecurringItem } from '@perfin/shared';
import { CalendarItemTile } from './CalendarItemTile';

interface MonthlyViewProps {
  items: Item[];
  offset: number;
  basePath?: string; // Optional - kept for backward compatibility
  showItemType?: boolean; // Show item type icon (useful for search results with mixed types)
}

interface DayItem {
  item: Item;
  isUnscheduled: boolean; // true if placed by createdAt instead of dueDate
  occurrenceDate?: Date;  // For recurring items, which occurrence this tile represents
}

interface DayCell {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  items: DayItem[];
}

// Helper to safely parse dates from Timestamp or Date objects
function parseDate(d: unknown): Date | null {
  if (!d) return null;
  if (typeof (d as { toDate?: () => Date }).toDate === 'function') {
    return (d as { toDate: () => Date }).toDate();
  }
  if (d instanceof Date) return d;
  if (typeof d === 'object' && 'seconds' in d) {
    return new Date((d as { seconds: number }).seconds * 1000);
  }
  return new Date(d as string | number);
}

export function MonthlyView({ items, offset, showItemType = false }: MonthlyViewProps) {
  const [expandedDay, setExpandedDay] = useState<string | null>(null);

  const { weeks } = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Target month based on offset
    const targetDate = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();

    const monthName = targetDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    // First day of month and last day of month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Start from Monday of the week containing the first day
    const startDate = new Date(firstDay);
    const firstDayOfWeek = firstDay.getDay();
    const mondayOffset = firstDayOfWeek === 0 ? -6 : 1 - firstDayOfWeek;
    startDate.setDate(firstDay.getDate() + mondayOffset);

    // End on Sunday of the week containing the last day
    const endDate = new Date(lastDay);
    const lastDayOfWeek = lastDay.getDay();
    const sundayOffset = lastDayOfWeek === 0 ? 0 : 7 - lastDayOfWeek;
    endDate.setDate(lastDay.getDate() + sundayOffset);

    const weeks: DayCell[][] = [];
    let currentWeek: DayCell[] = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const dayStart = new Date(currentDate);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(currentDate);
      dayEnd.setHours(23, 59, 59, 999);

      // Filter items for this day
      // - Recurring items: check occurrences[] array and create tile per occurrence
      // - Non-recurring items: use dueDate (or createdAt as fallback)
      const dayItems: DayItem[] = [];

      items.forEach((item) => {
        // Check if recurring item
        if (isRecurringItem(item)) {
          // Check occurrences that fall on this day
          const occurrences = (item as unknown as { occurrences?: Array<{ date: unknown }> }).occurrences;
          if (!occurrences || occurrences.length === 0) return;

          occurrences.forEach((occ) => {
            const occDate = parseDate(occ.date);
            if (!occDate) return;
            if (occDate >= dayStart && occDate <= dayEnd) {
              dayItems.push({
                item,
                isUnscheduled: false,
                occurrenceDate: occDate,
              });
            }
          });
          return;
        }

        // Non-recurring: use dueDate
        const dueDate = parseDate(item.dueDate);
        if (dueDate && dueDate >= dayStart && dueDate <= dayEnd) {
          dayItems.push({
            item,
            isUnscheduled: false,
          });
          return;
        }

        // No dueDate - use createdAt
        if (!dueDate) {
          const createdDate = parseDate(item.createdAt);
          if (createdDate && createdDate >= dayStart && createdDate <= dayEnd) {
            dayItems.push({
              item,
              isUnscheduled: true,
            });
          }
        }
      });

      currentWeek.push({
        date: new Date(currentDate),
        dayNumber: currentDate.getDate(),
        isCurrentMonth: currentDate.getMonth() === month,
        isToday: currentDate.getTime() === today.getTime(),
        items: dayItems,
      });

      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return { weeks, monthName };
  }, [items, offset]);

  const expandedDayData = expandedDay
    ? weeks.flat().find((d) => d.date.toISOString() === expandedDay)
    : null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Day names header */}
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
          <div
            key={day}
            className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-r border-gray-200 last:border-r-0"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="divide-y divide-gray-200">
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7">
            {week.map((day) => (
              <div
                key={day.date.toISOString()}
                className={clsx(
                  'min-h-[80px] p-1 border-r border-gray-200 last:border-r-0',
                  !day.isCurrentMonth && 'bg-gray-50',
                  day.isToday && 'bg-primary-50/50'
                )}
              >
                {/* Day number */}
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={clsx(
                      'text-sm font-medium w-6 h-6 flex items-center justify-center rounded-full',
                      day.isToday && 'bg-primary-600 text-white',
                      !day.isToday && day.isCurrentMonth && 'text-gray-900',
                      !day.isToday && !day.isCurrentMonth && 'text-gray-400'
                    )}
                  >
                    {day.dayNumber}
                  </span>
                  {day.items.length > 0 && (
                    <span className="text-xs text-gray-500">({day.items.length})</span>
                  )}
                </div>

                {/* Items preview */}
                <div className="space-y-0.5">
                  {day.items.slice(0, 2).map(({ item, isUnscheduled, occurrenceDate }) => (
                    <CalendarItemTile
                      key={occurrenceDate ? `${item.id}-${occurrenceDate.getTime()}` : item.id}
                      item={item}
                      variant="compact"
                      showType={showItemType}
                      isUnscheduled={isUnscheduled}
                      occurrenceDate={occurrenceDate}
                    />
                  ))}
                  {day.items.length > 2 && (
                    <button
                      onClick={() => setExpandedDay(day.date.toISOString())}
                      className="w-full text-left px-1 py-0.5 text-xs text-gray-500 hover:text-gray-700"
                    >
                      +{day.items.length - 2} more
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Expanded day modal */}
      {expandedDayData && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setExpandedDay(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="font-medium text-gray-900">
                {expandedDayData.date.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                })}
              </h3>
              <button
                onClick={() => setExpandedDay(null)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-2 overflow-y-auto max-h-[60vh]">
              {expandedDayData.items.map(({ item, isUnscheduled, occurrenceDate }) => (
                <CalendarItemTile
                  key={occurrenceDate ? `${item.id}-${occurrenceDate.getTime()}` : item.id}
                  item={item}
                  variant="expanded"
                  showType={showItemType}
                  isUnscheduled={isUnscheduled}
                  occurrenceDate={occurrenceDate}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
